import { EditorBootstrapError, EditorBootstrapErrorCode } from './errors';
import { loadMainPackages } from './loadMainPackages';

import type { EditorModel, EditorLoggers } from 'types/core';
import type { EditorStore } from '#packages/stateManagement';
import type { AppHost } from '#packages/apilib';
import type { EditorPermissions } from '#packages/editorPermissions';
import type { EditorParams } from '#packages/editorParams';
import type { EditorBaseHooks } from '#packages/editorCore';
import type { StartupConfig } from './common';

export async function initEditor(
  {
    editorLoggers,
    editorModel,
    editorParams,
    editorPermissions,
    editorBaseHooks,
  }: {
    editorLoggers: EditorLoggers;
    editorModel: EditorModel;
    editorParams: EditorParams;
    editorPermissions: EditorPermissions;
    editorBaseHooks: EditorBaseHooks;
  },
  config: StartupConfig,
) {
  const fedopsLogger = editorLoggers.fedops.logger;
  const { rEditor } = await loadMainPackages(
    { fedopsLogger, editorModel },
    config,
  );

  let builtEditor: {
    host: AppHost;
    store: EditorStore;
  };
  try {
    fedopsLogger.appLoadingPhaseStart('editor-build-app');
    builtEditor = await rEditor.editorMain.build({
      editorParams,
      editorLoggers,
      editorPermissions,
      editorBaseHooks,
    });
    fedopsLogger.appLoadingPhaseFinish('editor-build-app');
  } catch (error) {
    throw new EditorBootstrapError('Failed to build editor', {
      code: EditorBootstrapErrorCode.EditorBuildAppError,
      originalError: error,
    });
  }

  let unmount = () => {};

  if (config.shouldRender) {
    try {
      fedopsLogger.appLoadingPhaseStart('editor-render-app');
      ({ unmount } = rEditor.editorMain.render(builtEditor));
      fedopsLogger.appLoadingPhaseFinish('editor-render-app');
    } catch (error) {
      throw new EditorBootstrapError('Failed to render editor', {
        code: EditorBootstrapErrorCode.EditorRenderAppError,
        originalError: error,
      });
    }
  }

  const clean = () => unmount();
  return { builtEditor, clean };
}
